import type { ColumnType } from 'ant-design-vue/es/table'

// 视频列表
interface VideoListInfo {
    videoId: string
    // 封面
    cover: string
    // 标题
    title: string
    // 章节信息
    textbookGroupName?: string
    // 状态
    status?: number
    // 创建时间
    createTime?: string
}

// 章节信息
interface TextbookInfo {
    // 完整名称
    textbookGroupName?: string
    // 学段
    studyPeriod?: string
    // 年级
    gradeCode?: string
    // 学科
    subjectCode?: string
    // 册次
    volumeCode?: string
    // 版本
    versionId?: string
    // 教材
    textbookId?: string
    // 单元
    parentChapterId?: string
    // 章节
    chapterId?: string
}

// 新增视频
interface AddVideoInfo {
    videoId?: string
    // 封面
    cover: string
    // 标题
    title: string
    // 章节信息
    textbook?: TextbookInfo
    // 简介
    intro?: string
    // 附件
    attachUrl: string
    // 知识点
    knowledgeIds?: string[]
    // 教学课件
    coursewareIds?: string[]
    // 课后习题
    questionIds?: string[]
    // 课后试卷
    paperIds?: string[]
    // 辅助素材包 JSON数组
    materialUrl?: string
    // 视频时长
    playDuration?: number
}

// 视频详情
interface VideoDetailInfo {
    videoId: string
    // 封面
    cover: string
    // 标题
    title: string
    // 章节信息label
    textbookGroupName?: string
    // 状态
    status?: number

    // 简介
    intro?: string
    // 附件
    attachUrl: string
    // 知识点列表
    knowledgeIds?: string[]
    // 知识点label列表
    knowledge?: any[]
    // 教学课件列表
    coursewareIds?: string[]
    // 课后习题列表
    questionIds?: string[]
    // 课后试卷列表
    paperIds?: string[]
    // 辅助素材包
    packageList?: any[]
    // 辅助素材包-图片列表
    packageImgs?: string[]
    // 辅助素材包-文件列表
    packageFiles?: any[]

    // 创建人
    createBy?: string
    // 创建时间
    createTime?: string
    // 审核人员
    reviewer?: string
    // 审核时间
    reviewTime?: string
    // 审核备注
    remark?: string
    // 浏览量
    viewCount?: number
    // 收藏量
    collectCount?: number
    // 分享量
    shareCount?: number
    // 点赞量
    likeCount?: number
}

// 教学课件信息
interface CoursewareInfo {
    coursewareId: string
    // 封面
    cover: string
    // 标题
    title: string
    // 创建人
    createBy?: string
    // 创建时间
    createTime?: string
}

// 课后习题信息
interface ExerciseInfo {
    questionId: string
    // 题型
    questionType: string
    // 试题类型 1笔试题 2听力题
    type: string
    // 题干
    question: string
    // 选项
    options?: OptionInfo[]
    // 章节信息完整label
    textbookInfo?: string
    // 章节信息-学段
    period?: string
    // 章节信息-学段label   
    periodLabel?: string
    // 章节信息-年级
    gradeCode?: string
    // 章节信息-年级label
    gradeLabel?: string
    // 章节信息-学科
    subjectCode?: string
    // 章节信息-学科label
    subjectLabel?: string
    // 章节信息-版本
    version?: string
    // 章节信息-版本label
    versionLabel?: string
    // 章节信息-册次
    volume?: string
    // 章节信息-册次label
    volumeLabel?: string
    // 章节信息-教材
    textbook?: string
    // 章节信息-教材label
    textbookLabel?: string
    // 章节信息-单元
    unit?: string
    // 章节信息-单元label
    unitLabel?: string
    // 章节信息-章节
    chapter?: string
    // 章节信息-章节label
    chapterLabel?: string
}

// 选项信息
interface OptionInfo {
    // 选项
    option: string
    // 选项内容
    content: string
}

// 课后试卷信息
interface PaperInfo {
    id: number
    // 试卷名称
    title: string
    // 章节信息完整label
    textbookInfo?: string
    // 章节信息-学段
    period?: string
    // 章节信息-学段label   
    periodLabel?: string
    // 章节信息-年级
    gradeCode?: string
    // 章节信息-年级label
    gradeLabel?: string
    // 章节信息-学科
    subjectCode?: string
    // 章节信息-学科label
    subjectLabel?: string
    // 章节信息-版本
    version?: string
    // 章节信息-版本label
    versionLabel?: string
    // 章节信息-册次
    volume?: string
    // 章节信息-册次label
    volumeLabel?: string
    // 章节信息-教材
    textbook?: string
    // 章节信息-教材label
    textbookLabel?: string
    // 章节信息-单元
    unit?: string
    // 章节信息-单元label
    unitLabel?: string
    // 章节信息-章节
    chapter?: string
    // 章节信息-章节label
    chapterLabel?: string
    // 试卷形式:在线试卷/模拟试卷
    paperFormat: number
    // 试卷类型
    paperType: string
    // 试卷类型label
    paperTypeLabel?: string
    // 考试时长
    duration?: number
    // 是否乱序出题
    isOrder?: boolean
    // 是否有讲解视频
    isVideo?: boolean
    // 是否有答案
    isAnswer?: boolean
}

const columns: ColumnType<VideoListInfo>[] = [
    {
        title: '视频封面',
        key: 'cover',
    },
    {
        title: '视频标题',
        dataIndex: 'title',
    },
    {
        title: '章节信息',
        dataIndex: 'textbookGroupName',
    },
    {
        title: '状态',
        key: 'status',
    },
    {
        title: '创建时间',
        dataIndex: 'createTime',
    },
    {
        title: '操作',
        key: 'operation',
        width: 350
    },
]

const coursewareColumns: ColumnType<CoursewareInfo>[] = [
    {
        title: '课件封面',
        key: 'cover',
    },
    {
        title: '课件标题',
        dataIndex: 'title',
    },
    {
        title: '创建时间',
        dataIndex: 'createTime',
    },
    {
        title: '创建人',
        dataIndex: 'createBy',
    },
    {
        title: '操作',
        key: 'operation',
        width: 100
    },
]

export {
    VideoListInfo,
    TextbookInfo,
    AddVideoInfo,
    VideoDetailInfo,
    CoursewareInfo,
    ExerciseInfo,
    PaperInfo,
    columns,
    coursewareColumns
}